import type {
  BuildTransactionRequestInput,
  TransactionRequestBody,
} from '../interfaces/InvoiceService';

/**
 * Builder for the `POST /v2/invoices/:invoice_id/transactions` request body
 * (Phase 5.3, D-01/D-06). This is the INVERSE of `adaptInvoiceDetail`
 * (domain → request body); it maps the in-modal payment intent to the exact
 * per-method param set the controller permits.
 *
 * Wire-contract sources (read 2026-06-04, port research §"POST /transactions"):
 *   - ../api/app/controllers/v2/transactions_controller.rb:255-292 (permitted)
 *   - :106 (cvv_captured defaults true), :289-290 (*_save_payment_method cast)
 *   - :40,159,349-380 (apply_credit → TransactionInstallmentAllocator for PB)
 *   - ../api/spec/requests/v2/transactions_requests_spec.rb (param fixtures)
 *
 * Per-method param sets (emit-only-what-the-method-needs; omit-vs-preserve):
 *   - card (GPI or Stax — gateway is server-side): `transaction_type:'card'`,
 *     `amount_cents`, `card_token`, `name`, `street`, `city`, `state` (2-char),
 *     `zip_code`, `card_save_payment_method`, `cvv_captured` (default true).
 *   - ach (Stax): `transaction_type:'ach'`, `amount_cents`, `ach_token`,
 *     `ach_save_payment_method`. NO billing address.
 *   - ach (GPI raw — Plan 05.3-10, legacy parity): `transaction_type:'ach'`,
 *     `amount_cents`, raw `ach_routing_number` / `ach_account_number` /
 *     `ach_account_type` ('checking'|'savings') / `ach_first_name` /
 *     `ach_last_name`, `ach_save_payment_method`. NO `ach_token` (GPI has no
 *     browser tokenize; the server tokenizes/charges via charge_ach_explicit).
 *     Param names are the controller's permitted ACH set (transactions_controller
 *     .rb:275-284). The body still never names the processor.
 *   - payment_method (saved-method selection, Plan 05.3-12): the SIMPLEST path —
 *     `transaction_type:'payment_method'`, `amount_cents`, `next_payment_key`,
 *     `payment_method_id` (+ `apply_credit` for PB). NO token, NO card/ACH
 *     fields, NO save flag, NO billing address. The server looks the saved
 *     `PaymentMethod` up by id, authorizes ownership, and charges its stored
 *     permanent token. `[VERIFIED: transactions_controller.rb:127-139, 255-292]`.
 *   - PB (progress billing, D-06): `amount_cents = currentDrawRemainingCents`
 *     (FIXED, non-editable) + `apply_credit:true`. NO `draw_id` — the server
 *     allocator owns draw allocation.
 *
 * The save decision rides the charge (`card_save_payment_method` /
 * `ach_save_payment_method`, D-01) — works for both GPI single-use and Stax
 * reusable tokens; supersedes AC#9's post-payment prompt.
 *
 * The body NEVER names the processor and carries no dead-code payment paths
 * (threats T-05.3-04/05) — the server resolves the processor from tenant config.
 * `next_payment_key` is emitted on the body for completeness, but the SERVICE
 * places it as a URL query param (idempotency — there is NO `Idempotency-Key`
 * header).
 */

/**
 * The (caller-clamped) entered amount is what we post — for PB as well as
 * standard. PB is now editable (supersedes the fixed D-06 amount): the customer
 * may pay part of the current draw (remainder stays due) or more than it (the
 * overage rolls forward), and the server's `apply_credit` allocator splits the
 * `amount_cents` across draws. The caller has already clamped `amountCents` to
 * `balanceCents` (no client overpay; the server 409s as the authoritative
 * backstop — T-05.3-03). Falls back to the current draw remaining only when a
 * PB caller omits `amountCents` (defensive — the modal always supplies it).
 */
function resolveAmountCents(input: BuildTransactionRequestInput): number {
  if (input.amountCents != null) return input.amountCents;
  return input.progressBilling?.currentDrawRemainingCents ?? 0;
}

/**
 * ACH param mapping (extracted to keep `buildTransactionRequest` under the
 * cyclomatic-complexity cap). GPI emits the raw bank fields with NO token (the
 * server tokenizes/charges via charge_ach_explicit). Stax emits a
 * browser-vaulted `ach_token` AND the raw fields together — legacy parity: the
 * Stax ACH charge 500s if the raw bank fields are omitted even when a token is
 * present. So token and fields are NOT mutually exclusive: send whichever the
 * caller provides.
 */
function applyAchFields(
  body: TransactionRequestBody,
  input: BuildTransactionRequestInput,
): void {
  if (input.achToken != null) {
    body.ach_token = input.achToken;
  }
  if (input.achFields) {
    body.ach_routing_number = input.achFields.routingNumber;
    body.ach_account_number = input.achFields.accountNumber;
    body.ach_account_type = input.achFields.accountType;
    body.ach_first_name = input.achFields.firstName;
    body.ach_last_name = input.achFields.lastName;
    if (input.achFields.holderType != null) {
      body.ach_holder_type = input.achFields.holderType;
    }
  }
  body.ach_save_payment_method = input.savePaymentMethod === true;
}

export function buildTransactionRequest(
  input: BuildTransactionRequestInput,
): TransactionRequestBody {
  const body: TransactionRequestBody = {
    transaction_type: input.transactionType,
    amount_cents: resolveAmountCents(input),
    next_payment_key: input.nextPaymentKey,
  };

  if (input.transactionType === 'card') {
    if (input.cardToken != null) body.card_token = input.cardToken;
    if (input.name != null) body.name = input.name;
    if (input.street != null) body.street = input.street;
    if (input.city != null) body.city = input.city;
    // Server expects a 2-char state code — truncate defensively.
    if (input.state != null) body.state = input.state.slice(0, 2);
    if (input.zipCode != null) body.zip_code = input.zipCode;
    body.card_save_payment_method = input.savePaymentMethod === true;
    // cvv_captured defaults true at the controller (:106); the hosted-field
    // SDKs capture the CVV, so the client asserts it explicitly.
    body.cvv_captured = true;
  } else if (input.transactionType === 'payment_method') {
    // Saved-method charge (Plan 05.3-12): emit ONLY the on-file method id. The
    // server charges its stored permanent token — no token, no card/ACH fields,
    // no save flag (re-saving an already-saved method is meaningless), no
    // billing address. The base object + the PB apply_credit tail cover the rest.
    body.payment_method_id = input.paymentMethodId;
  } else {
    applyAchFields(body, input);
  }

  // PB credit allocation (D-06) — the client only flags it; the server's
  // TransactionInstallmentAllocator does the work. No draw_id.
  if (input.progressBilling) body.apply_credit = true;

  return body;
}
